import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { Mail } from "lucide-react";
import { BrandMark } from "../BrandMark";
import { scrollToSection, scrollToTop } from "./scrollToSection";
import { openCookiePreferences } from "./cookiePreferences";

const CONTACT_EMAIL = "hello@adambusinesshub.com";

type Column = {
  heading: "product" | "legal" | "contact";
  links: Array<{
    key: string;
    to: string;
    external?: boolean;
    section?: boolean;
    action?: boolean;
  }>;
};

const COLUMNS: Column[] = [
  {
    heading: "product",
    links: [
      { key: "features", to: "#capabilities", section: true },
      { key: "verticals", to: "#verticals", section: true },
      { key: "earlyAccess", to: "#pricing", section: true },
      { key: "faq", to: "#faq", section: true },
    ],
  },
  {
    heading: "legal",
    links: [
      { key: "terms", to: "/terms" },
      { key: "privacy", to: "/privacy" },
      { key: "dpa", to: "/dpa" },
      { key: "aup", to: "/acceptable-use" },
      { key: "subprocessors", to: "/subprocessors" },
      { key: "cookies", to: "/cookies" },
      { key: "cookiePreferences", to: "#", action: true },
    ],
  },
  {
    heading: "contact",
    links: [
      { key: "contact", to: `mailto:${CONTACT_EMAIL}`, external: true },
      { key: "consulting", to: "mailto:sales@adambusinesshub.com", external: true },
      { key: "login", to: "/login" },
    ],
  },
];

export function LandingFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const lang = (i18n.language ?? "en").split("-")[0];

  return (
    <footer className="relative border-t border-slate-200/80 bg-white pt-16 pb-10 dark:border-white/5 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div className="max-w-sm">
            <Link
              to="/"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
              className="flex items-center gap-2"
            >
              <BrandMark />
              <span className="text-lg font-bold text-slate-900 dark:text-white">Adam</span>
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {t("landing.footer.tagline")}
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-5 inline-flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
            >
              <Mail size={15} />
              {CONTACT_EMAIL}
            </a>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                {t(`landing.footer.columns.${col.heading}`)}
              </p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.key}>
                    {l.action ? (
                      <button
                        type="button"
                        onClick={openCookiePreferences}
                        className="text-left text-sm text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                      >
                        {t(`landing.footer.links.${l.key}`)}
                      </button>
                    ) : l.section ? (
                      <a
                        href={l.to}
                        onClick={(e) => {
                          // Scroll without writing the hash into the URL.
                          e.preventDefault();
                          scrollToSection(l.to);
                        }}
                        className="text-sm text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                      >
                        {t(`landing.footer.links.${l.key}`)}
                      </a>
                    ) : l.external ? (
                      <a
                        href={l.to}
                        className="text-sm text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                      >
                        {t(`landing.footer.links.${l.key}`)}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="text-sm text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                      >
                        {t(`landing.footer.links.${l.key}`)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-slate-200/80 pt-6 text-xs text-slate-500 dark:border-white/5 sm:flex-row">
          <p>{t("landing.footer.copyright", { year })}</p>
          <div className="flex items-center rounded-full bg-slate-100 p-0.5 ring-1 ring-slate-200 dark:bg-white/5 dark:ring-white/10">
            {(["en", "es"] as const).map((code) => (
              <button
                key={code}
                onClick={() => i18n.changeLanguage(code)}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-all ${
                  lang === code
                    ? "bg-blue-600 text-white"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
